import { SiteMetadata } from '@/api/fe-api/domain/entities/site-metadata';
import { useMe } from '@/hooks/use-me';
import { UnreachableCaseError } from '@/utils/errors/unreachable-case-error';
import {
  formatAmpere,
  formatAmpereHours,
  formatEnergy,
  formatPower,
  formatVolt,
} from '@/utils/format/battery';
import { formatDate, formatDatetime, formatYear } from '@/utils/format/date';
import { deviceType } from '@/utils/format/device-type';
import { formatNumber, formatPercentage } from '@/utils/format/number';
import { formatTemperature } from '@/utils/format/temperature';

export type SiteMetadataValueProps = {
  value: SiteMetadata.Value;
};

/**
 * INFO: Since metadata numbers have varying decimal places, we have to set a high maximum fraction
 * digits
 * Set to 1 decimal place, to allow enough precision on lower levels, while not showing excessive
 * precision on higher levels.
 */
const MAX_FRACTION_DIGITS = 1;

export function SiteMetadataValue({ value }: SiteMetadataValueProps) {
  const me = useMe();

  switch (value.type) {
    case 'string': {
      switch (value.format) {
        case 'type': {
          return <>{deviceType(value.value)}</>;
        }
        default: {
          return <>{value.value}</>;
        }
      }
    }
    case 'number': {
      if (!value.format) {
        return <>{formatNumber(value.value, 0, MAX_FRACTION_DIGITS)}</>;
      }

      switch (value.format) {
        case 'watt': {
          return (
            <>
              {formatPower(
                value.value,
                me.config.powerUnit,
                0,
                MAX_FRACTION_DIGITS,
              )}
            </>
          );
        }
        case 'watt_hour': {
          return (
            <>
              {formatEnergy(
                value.value,
                me.config.powerUnit,
                0,
                MAX_FRACTION_DIGITS,
              )}
            </>
          );
        }
        case 'volt': {
          return <>{formatVolt(value.value, 0, MAX_FRACTION_DIGITS)}</>;
        }
        case 'ampere': {
          return <>{formatAmpere(value.value, 0, MAX_FRACTION_DIGITS)}</>;
        }
        case 'ampere_hour': {
          return <>{formatAmpereHours(value.value, 0, MAX_FRACTION_DIGITS)}</>;
        }
        case 'celsius': {
          return <>{formatTemperature(value.value, 0, MAX_FRACTION_DIGITS)}</>;
        }
        case 'percent': {
          return <>{formatPercentage(value.value, 0, MAX_FRACTION_DIGITS)}</>;
        }
        case 'year': {
          return <>{formatYear(value.value)}</>;
        }
        default: {
          throw new UnreachableCaseError(value.format);
        }
      }
    }
    case 'date': {
      switch (value.format) {
        case 'date': {
          return <>{formatDate(value.value)}</>;
        }
        case 'datetime': {
          return <>{formatDatetime(value.value)}</>;
        }
        default: {
          throw new UnreachableCaseError(value);
        }
      }
    }
    case 'cron': {
      return <>{value.value}</>;
    }
    case 'unknown': {
      return <pre>{JSON.stringify(value.value, null, 2)}</pre>;
    }
    default: {
      throw new UnreachableCaseError(value);
    }
  }
}
